import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  NbMediaBreakpointsService,
  NbMenuItem,
  NbMenuService,
  NbSidebarService,
  NbThemeService,
  NbToastrService,
} from '@nebular/theme';

import { UserData } from '../../../@core/data/users';
import { LayoutService } from '../../../@core/utils';
import { map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
import { ProfileService } from '../../../services/profile/profile.service';
import { ApplicationLauncherService } from '../../../services/application-launcher/application-launcher.service';

interface ApplicationConfig {
  id?: string | number;
  application_id?: string | number;
  name?: string;
  application_name?: string;
  title?: string;
  description?: string;
  subtitle?: string;
  url?: string;
  application_url?: string;
  icon?: string;
  accent?: string;
  current?: boolean;
  active?: boolean;
  status?: number;
  openInNewTab?: boolean;
  open_in_new_tab?: boolean;
  allowedRoles?: Array<number | string>;
  allowed_roles?: Array<number | string>;
  ssoEndpoint?: string;
  sso_endpoint?: string;
  codeField?: string;
  code_field?: string;
}

interface ApplicationLink {
  id: string;
  name: string;
  description: string;
  url: string;
  icon: string;
  accent: 'primary' | 'success' | 'info' | 'warning';
  current: boolean;
  openInNewTab: boolean;
  allowedRoles?: Array<number | string>;
  ssoEndpoint?: string;
  codeField: string;
  ssoLoading: boolean;
  ssoError: boolean;
}

@Component({
  selector: 'ngx-header',
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit, OnDestroy {

  @ViewChild('appLauncher') appLauncher?: ElementRef<HTMLElement>;

  private destroy$: Subject<void> = new Subject<void>();
  userPictureOnly: boolean = false;
  user: any;
  displayName: string = 'User';
  displayRole: string = 'User';
  isAppLauncherOpen = false;
  applications: ApplicationLink[] = [];
  private readonly configuredApplications: ApplicationConfig[] = [
    {
      id: 'cmis5',
      name: 'CMIS 5',
      description: 'Central management information system',
      url: '/pages/dashboard',
      icon: 'grid-outline',
      accent: 'primary',
      current: true,
      openInNewTab: true,
    },
    {
      id: 'data-warehouse',
      name: 'Data Warehouse',
      description: 'Analytics, insights, and consolidated reports',
      url: 'https://prod-anudip-analytics.tekdinext.com/sso/cmis/callback?code={short_code}',
      icon: 'bar-chart-2-outline',
      accent: 'info',
      openInNewTab: true,
      ssoEndpoint: 'report/sso-for-data-wirehouse',
      codeField: 'sso_token.code',
    },
  ];

  themes = [
    {
      value: 'default',
      name: 'Light',
    },
    {
      value: 'dark',
      name: 'Dark',
    },
    {
      value: 'cosmic',
      name: 'Cosmic',
    },
    {
      value: 'corporate',
      name: 'Corporate',
    },
  ];

  currentTheme = 'default';

  userMenu: NbMenuItem[] = [];

  constructor(private sidebarService: NbSidebarService,
              private menuService: NbMenuService,
              private themeService: NbThemeService,
              private userService: UserData,
              private layoutService: LayoutService,
              private profileService: ProfileService,
              private applicationLauncherService: ApplicationLauncherService,
              private toastrService: NbToastrService,
              private router: Router,
              private breakpointService: NbMediaBreakpointsService) {
  }

  public user_image = `${environment.s3BucketUrl}uploads/user_image/`;
  private readonly maleAvatar = 'assets/images/male.png';
  private readonly femaleAvatar = 'assets/images/female.png';


  ngOnInit() {
    this.currentTheme = this.themeService.currentTheme;

    const storedUser = localStorage.getItem('user');
    let parsedUser: any = {};
    if (storedUser) {
      parsedUser = JSON.parse(storedUser);
      const member = parsedUser?.member;
      const gender = member?.gender || parsedUser?.gender || '';
      const roleId = parsedUser?.cmis_role ?? parsedUser?.role_id;
      const userName = `${parsedUser?.first_name ?? member?.first_name ?? ''} ${parsedUser?.last_name ?? member?.last_name ?? ''}`.trim()
        || parsedUser?.name
        || 'User';
      const roleName = parsedUser?.role_name || this.getRoleName(roleId);

      const userImagePath = parsedUser?.user_image
        ? this.user_image + parsedUser.user_image
        : this.getGenderAvatar(gender);

      this.user = {
        name: userName,
        picture: userImagePath,
      };

      this.displayName = userName;
      this.displayRole = roleName;

      this.userMenu = [
        { title: 'Profile' },
        { title: 'Change Password' },
        { title: 'Log out' },
      ];
    } else {
      this.displayName = 'User';
      this.displayRole = 'User';
      this.userMenu = [
        { title: 'Profile' },
        { title: 'Change Password' },
        { title: 'Log out' },
      ];
    }

    this.loadApplications(parsedUser);

    const { sm } = this.breakpointService.getBreakpointsMap();
    this.themeService.onMediaQueryChange()
      .pipe(
        map(([, currentBreakpoint]) => currentBreakpoint.width < sm),
        takeUntil(this.destroy$),
      )
      .subscribe((isSmallScreen: boolean) => this.userPictureOnly = isSmallScreen);

    this.themeService.onThemeChange()
      .pipe(
        map(({ name }) => name),
        takeUntil(this.destroy$),
      )
      .subscribe(themeName => this.currentTheme = themeName);

    // Menu options profile
    this.menuService.onItemClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ item: { title } }) => {
        if (title === 'Log out') {
          this.logout();
        } else if (title === 'Profile') {
          this.goToProfile();
        } else if (title === 'Change Password') {
          this.goToChangePassword();
        }
      });
  }

  logout() {
    this.profileService.logout().subscribe({
      next: () => {
        this.completeLogout();
      },
      error: () => {
        // Even if API logout fails, clear local session so user can proceed.
        this.completeLogout();
      },
    });
  }

  private completeLogout(): void {
    this.profileService.clearStoredUser();
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_app_token');
    localStorage.removeItem('token');
    this.router.navigate(['/auth/login']);
  }

  goToProfile() {
    this.router.navigate(['/pages/profile']);
  }

  goToChangePassword() {
    this.router.navigate(['/pages/change-password']);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  changeTheme(themeName: string) {
    this.themeService.changeTheme(themeName);
  }

  toggleSidebar(): boolean {
    this.sidebarService.toggle(true, 'menu-sidebar');
    this.layoutService.changeLayoutSize();

    return false;
  }

  navigateHome() {
    this.menuService.navigateHome();
    return false;
  }

  toggleAppLauncher(event: Event): void {
    event.stopPropagation();
    this.isAppLauncherOpen = !this.isAppLauncherOpen;
  }

  closeAppLauncher(): void {
    this.isAppLauncherOpen = false;
  }

  openApplication(application: ApplicationLink): void {
    if (application.ssoEndpoint) {
      const pendingWindow = application.openInNewTab
        ? window.open('/assets/sso-loading.html', '_blank')
        : null;

      if (pendingWindow) {
        pendingWindow.opener = null;
      }

      this.fetchSsoCode(application, pendingWindow);
      return;
    }

    this.closeAppLauncher();

    if (this.isInternalUrl(application.url)) {
      if (application.openInNewTab) {
        this.openExternalUrl(application.url, true);
      } else {
        this.router.navigateByUrl(application.url);
      }
      return;
    }

    if (application.openInNewTab) {
      const newWindow = window.open(application.url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
      return;
    }

    window.location.assign(application.url);
  }

  trackApplication(_index: number, application: ApplicationLink): string {
    return application.id;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.isAppLauncherOpen && !this.appLauncher?.nativeElement.contains(event.target as Node)) {
      this.closeAppLauncher();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeAppLauncher();
  }

  private getGenderAvatar(gender: string): string {
    const normalizedGender = (gender || '').toLowerCase();

    if (normalizedGender === 'female' || normalizedGender === 'f') {
      return this.femaleAvatar;
    }

    return this.maleAvatar;
  }

  private getRoleName(roleId: number | string): string {
    const roleMap: { [key: string]: string } = {
      '15': 'Admin',
      '17': 'Super Admin',
      '23': 'Placement Officer',
      '34': 'Counselor',
      '51': 'Marcom',
    };

    return roleMap[String(roleId)] || 'User';
  }

  private loadApplications(user: any): void {
    this.applications = this.buildApplications(user, this.configuredApplications);
  }

  private buildApplications(user: any, configuredApplications: ApplicationConfig[]): ApplicationLink[] {
    const userApplications = this.getUserApplications(user);
    const roleId = user?.cmis_role ?? user?.role_id;
    const uniqueApplications = new Map<string, ApplicationLink>();

    [...configuredApplications, ...userApplications]
      .map((application: any, index: number) => this.normalizeApplication(application, index))
      .filter((application: ApplicationLink | null): application is ApplicationLink => application !== null)
      .filter((application: ApplicationLink) => this.canAccessApplication(application, roleId))
      .forEach((application: ApplicationLink) => {
        const key = application.id || application.url;
        uniqueApplications.set(key, application);
      });

    return Array.from(uniqueApplications.values());
  }

  private getUserApplications(user: any): any[] {
    const possibleLists = [
      user?.applications,
      user?.apps,
      user?.application_access,
    ];

    return possibleLists.find((list: any) => Array.isArray(list)) || [];
  }

  private normalizeApplication(application: any, index: number): ApplicationLink | null {
    if (!application || application.active === false || application.status === 0) {
      return null;
    }

    const url = String(application.url || application.application_url || '').trim();
    if (!url || !this.isSupportedUrl(url)) {
      return null;
    }

    const name = String(application.name || application.application_name || application.title || 'Application');
    const accent = ['primary', 'success', 'info', 'warning'].includes(application.accent)
      ? application.accent
      : 'primary';

    return {
      id: String(application.id || application.application_id || `application-${index}`),
      name,
      description: String(application.description || application.subtitle || 'Open application'),
      url,
      icon: String(application.icon || 'external-link-outline'),
      accent,
      current: Boolean(application.current),
      openInNewTab: application.openInNewTab !== false && application.open_in_new_tab !== false,
      allowedRoles: Array.isArray(application.allowedRoles)
        ? application.allowedRoles
        : Array.isArray(application.allowed_roles)
          ? application.allowed_roles
          : undefined,
      ssoEndpoint: String(application.ssoEndpoint || application.sso_endpoint || '').trim() || undefined,
      codeField: String(application.codeField || application.code_field || 'short_code'),
      ssoLoading: false,
      ssoError: false,
    };
  }

  private fetchSsoCode(application: ApplicationLink, pendingWindow: Window | null): void {
    if (!application.ssoEndpoint || application.ssoLoading) {
      pendingWindow?.close();
      return;
    }

    application.ssoLoading = true;
    application.ssoError = false;

    this.applicationLauncherService.getSsoCode(application.ssoEndpoint, application.codeField)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (shortCode) => {
          application.ssoLoading = false;
          this.openSsoApplication(application, shortCode, pendingWindow);
        },
        error: () => {
          application.ssoLoading = false;
          application.ssoError = true;
          pendingWindow?.close();
          this.toastrService.danger(
            'Secure access could not be prepared. Please try again.',
            application.name,
          );
        },
      });
  }

  private openSsoApplication(
    application: ApplicationLink,
    shortCode: string,
    pendingWindow: Window | null,
  ): void {
    const destinationUrl = application.url.replace(
      '{short_code}',
      encodeURIComponent(shortCode),
    );

    this.closeAppLauncher();

    if (pendingWindow && !pendingWindow.closed) {
      pendingWindow.location.replace(destinationUrl);
      return;
    }

    this.openExternalUrl(destinationUrl, application.openInNewTab);
  }

  private openExternalUrl(url: string, openInNewTab: boolean): void {
    if (openInNewTab) {
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
      return;
    }

    window.location.assign(url);
  }

  private canAccessApplication(application: ApplicationLink, roleId: number | string): boolean {
    if (!application.allowedRoles?.length) {
      return true;
    }

    return application.allowedRoles.some((allowedRole) => String(allowedRole) === String(roleId));
  }

  private isInternalUrl(url: string): boolean {
    return url.startsWith('/') && !url.startsWith('//');
  }

  private isSupportedUrl(url: string): boolean {
    if (this.isInternalUrl(url)) {
      return true;
    }

    try {
      const parsedUrl = new URL(url);
      return parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:';
    } catch {
      return false;
    }
  }
}
